// src/components/Common/Header.js

import React, { useState, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DonateModal from "./DonateModal";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import {
  FaSignOutAlt,
  FaUserShield,
  FaHome,
  FaUser,
  FaSignInAlt,
  FaBars,
  FaTimes,
  FaStar, // Import FaStar for the Favorites icon
} from "react-icons/fa";

export default function Header() {
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [isFallingHeartsVisible, setIsFallingHeartsVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { handleLogout, userRole } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine if the current page is Favorites
  const isFavoritesPage = location.pathname === "/favorites";

  const openDonateModal = () => {
    setIsDonateModalOpen(true);
  };

  const closeDonateModal = () => {
    setIsDonateModalOpen(false);
  };

  const onLogout = async () => {
    try {
      await handleLogout();
      addToast("You have successfully logged out.", "success");
    } catch (error) {
      addToast("An error occurred during logout. Please try again.", "error");
    } finally {
      navigate("/login", { replace: true });
      setIsMenuOpen(false);
    }
  };

  const handleMouseEnter = () => {
    setIsFallingHeartsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsFallingHeartsVisible(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  const isAdminPanel = location.pathname.startsWith("/admin");
  const isRoleSelection = location.pathname.startsWith("/role-selection");
  const isProfilePage = location.pathname.startsWith("/profile");
  const isGuest = userRole === "Guest";

  const renderNavigationLinks = (isMenu) => (
    <>
      {userRole === "Admin" && !isAdminPanel && !isRoleSelection && (
        <Link
          to="/admin"
          onClick={handleLinkClick}
          className={`bg-accentOrange text-white font-medium py-2 px-4 rounded-lg shadow-md hover:bg-accentOrangeDark transition-all duration-200 ease-in-out transform hover:scale-105 flex items-center space-x-1 ${
            isMenu ? "w-full justify-center" : ""
          }`}
          aria-label="Admin Panel"
        >
          <FaUserShield />
          <span>Admin Panel</span>
        </Link>
      )}

      {userRole === "Admin" && (isAdminPanel || isRoleSelection) && (
        <Link
          to="/"
          onClick={handleLinkClick}
          className={`bg-accentBlue text-white font-medium py-2 px-4 rounded-lg shadow-md hover:bg-accentBlueDark transition-all duration-200 ease-in-out transform hover:scale-105 flex items-center space-x-1 ${
            isMenu ? "w-full justify-center" : ""
          }`}
          aria-label="Main Page"
        >
          <FaHome />
          <span>Main Page</span>
        </Link>
      )}

      {userRole === "User" && (
        <>
          {!isProfilePage ? (
            <Link
              to="/profile"
              onClick={handleLinkClick}
              className={`bg-accentBlue text-white font-medium py-2 px-4 rounded-lg shadow-md hover:bg-accentBlueDark transition-all duration-200 ease-in-out transform hover:scale-105 flex items-center space-x-1 ${
                isMenu ? "w-full justify-center" : ""
              }`}
              aria-label="Profile"
            >
              <FaUser />
              <span>Profile</span>
            </Link>
          ) : (
            <Link
              to="/"
              onClick={handleLinkClick}
              className={`bg-accentBlue text-white font-medium py-2 px-4 rounded-lg shadow-md hover:bg-accentBlueDark transition-all duration-200 ease-in-out transform hover:scale-105 flex items-center space-x-1 ${
                isMenu ? "w-full justify-center" : ""
              }`}
              aria-label="Main Page"
            >
              <FaHome />
              <span>Main Page</span>
            </Link>
          )}
        </>
      )}

      {/* Favorites or Main Page Link based on current page */}
      {!isFavoritesPage ? (
        <Link
          to="/favorites"
          onClick={handleLinkClick}
          className={`bg-yellow-500 text-white font-medium py-2 px-4 rounded-lg shadow-md hover:bg-yellow-600 transition-all duration-200 ease-in-out transform hover:scale-105 flex items-center space-x-1 ${
            isMenu ? "w-full justify-center" : ""
          }`}
          aria-label="Favorites"
        >
          <FaStar />
          <span>Favorites</span>
        </Link>
      ) : (
        <Link
          to="/"
          onClick={handleLinkClick}
          className={`bg-accentBlue text-white font-medium py-2 px-4 rounded-lg shadow-md hover:bg-accentBlueDark transition-all duration-200 ease-in-out transform hover:scale-105 flex items-center space-x-1 ${
            isMenu ? "w-full justify-center" : ""
          }`}
          aria-label="Main Page"
        >
          <FaHome />
          <span>Main Page</span>
        </Link>
      )}

      {/* Donate Button */}
      <div className={`relative ${isMenu ? "w-full" : ""}`}>
        <button
          className={`bg-accentBlue text-white font-medium py-2 px-6 rounded-lg shadow-md flex items-center space-x-2 transform hover:scale-105 transition duration-200 relative overflow-hidden ${
            isMenu ? "w-full justify-center" : ""
          }`}
          onClick={() => {
            openDonateModal();
            handleLinkClick();
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          aria-label="Donate"
        >
          <span>Donate</span>
          <span className="animate-pulse">❤️</span>
        </button>

        {isFallingHeartsVisible && (
          <div className="falling-hearts-container absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="falling-heart"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${Math.random() * 3 + 2}s`,
                }}
              >
                ❤️
              </div>
            ))}
          </div>
        )}
      </div>

      {isGuest ? (
        <Link
          to="/login"
          onClick={handleLinkClick}
          className={`bg-accentBlue text-white font-medium py-2 px-4 rounded-lg shadow-md hover:bg-accent hover:bg-opacity-90 transition-all duration-200 ease-in-out transform hover:scale-105 flex items-center space-x-1 ${
            isMenu ? "w-full justify-center" : ""
          }`}
          aria-label="Login"
        >
          <FaSignInAlt />
          <span>Login</span>
        </Link>
      ) : (
        <button
          onClick={onLogout}
          className={`bg-red-600 text-white font-medium py-2 px-4 rounded-lg shadow-md hover:bg-red-500 transition duration-200 flex items-center space-x-1 ${
            isMenu ? "w-full justify-center" : ""
          }`}
          aria-label="Logout"
        >
          <FaSignOutAlt />
          <span>Logout</span>
        </button>
      )}
    </>
  );

  return (
    <header className="relative flex justify-between items-center w-full py-4 px-6 bg-transparent text-white z-50">
{/* Title Section */}
<Link to="/" className="text-xl font-semibold text-accentBlue flex items-center">
  <img
    src="/mf-nobg.png"
    alt="MF Logo"
    className="w-6 h-6 mr-2" // adjust as needed
  />
  <span className="text-white">M</span>ovie <span className="text-white">F</span>inder
</Link>

      {/* Hamburger Menu Button (Visible on small screens) */}
      <div className="sm:hidden z-50">
        <button
          onClick={toggleMenu}
          className="focus:outline-none"
          aria-label="Toggle Menu"
        >
          {isMenuOpen ? (
            <FaTimes className="w-6 h-6 transition-transform duration-300 transform" />
          ) : (
            <FaBars className="w-6 h-6 transition-transform duration-300 transform" />
          )}
        </button>
      </div>

      {/* Navigation Links (Visible on medium and larger screens) */}
      <nav className="hidden sm:flex space-x-4 z-20">
        {renderNavigationLinks(false)}
      </nav>

      {/* Sliding Menu (Visible on small screens when menu is open) */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-gray-900 text-white transform ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out z-40`}
      >
        <div className="flex flex-col mt-16 space-y-4 px-4">
          {renderNavigationLinks(true)}
        </div>
      </div>

      {/* Overlay when menu is open */}
      {isMenuOpen && (
        <div
          onClick={toggleMenu}
          className="fixed inset-0 bg-black opacity-50 z-30"
        ></div>
      )}

      {/* Light and Particles Positioned Behind Header */}
      <div className="absolute top-0 left-0 w-full h-40 flex justify-center items-center z-0 pointer-events-none">
        {/* Light Source Fading Out */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-3/4 h-40 bg-gradient-to-b from-[#4a90e2] via-transparent to-transparent opacity-60 blur-3xl"></div>
      </div>

      {/* Donate Modal */}
      <DonateModal isOpen={isDonateModalOpen} onClose={closeDonateModal} />
    </header>
  );
}
